import React, { Fragment, useEffect, useMemo, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import {
  Row,
  Col,
  Card,
  Table,
  Form,
  InputGroup,
  Button,
  Modal,
  Badge,
  Alert,
} from "react-bootstrap";
import {
  Eye,
  PencilSquare,
  Trash,
  ArrowClockwise,
  FileEarmarkPdf,
  PersonLinesFill,
} from "react-bootstrap-icons";

const BASE_URL = "https://admin.vascularirdoctors.com/backend";

const MembershipApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [membershipTypeFilter, setMembershipTypeFilter] = useState("");

  const [alert, setAlert] = useState({
    show: false,
    type: "",
    text: "",
  });

  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [viewApplication, setViewApplication] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [editApplication, setEditApplication] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const showAlert = (type, text) => {
    setAlert({ show: true, type, text });
    setTimeout(() => {
      setAlert({ show: false, type: "", text: "" });
    }, 3000);
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/membership-applications`);
      const data = await res.json();
      setApplications(data);
    } catch (err) {
      console.log("FETCH MEMBERSHIP APPLICATIONS ERROR:", err);
      showAlert("danger", "Unable to fetch membership applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const uniqueMembershipTypes = useMemo(() => {
    const types = applications
      .map((item) => item.membership_type)
      .filter(Boolean);
    return [...new Set(types)];
  }, [applications]);

  const filteredApplications = useMemo(() => {
    return applications.filter((item) => {
      const fullName =
        `${item.first_name || ""} ${item.middle_name || ""} ${item.last_name || ""}`.toLowerCase();

      const matchesSearch =
        fullName.includes(search.toLowerCase()) ||
        item.email?.toLowerCase().includes(search.toLowerCase()) ||
        item.phone_number?.toLowerCase().includes(search.toLowerCase()) ||
        item.membership_type?.toLowerCase().includes(search.toLowerCase()) ||
        item.medical_council_no?.toLowerCase().includes(search.toLowerCase());

      const matchesMembershipType = membershipTypeFilter
        ? item.membership_type === membershipTypeFilter
        : true;

      return matchesSearch && matchesMembershipType;
    });
  }, [applications, search, membershipTypeFilter]);

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const deleteApplication = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/membership-applications/${deleteId}`,
        {
          method: "DELETE",
        }
      );

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Membership application deleted successfully.");
        fetchApplications();
      } else {
        showAlert("danger", result.message || "Delete failed.");
      }
    } catch (err) {
      console.log("DELETE MEMBERSHIP APPLICATION ERROR:", err);
      showAlert("danger", "Unable to delete application.");
    }

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const viewDetails = (application) => {
    setViewApplication(application);
    setShowViewModal(true);
  };

  const openEditModal = (application) => {
    setEditApplication({
      ...application,
    });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditApplication((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateApplication = async () => {
    try {
      setEditLoading(true);

      const res = await fetch(
        `${BASE_URL}/membership-applications/${editApplication.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editApplication),
        }
      );

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Membership application updated successfully.");
        setShowEditModal(false);
        setEditApplication(null);
        fetchApplications();
      } else {
        showAlert("danger", result.message || "Update failed.");
      }
    } catch (err) {
      console.log("UPDATE MEMBERSHIP APPLICATION ERROR:", err);
      showAlert("danger", "Unable to update application.");
    } finally {
      setEditLoading(false);
    }
  };

  const downloadPDF = (pdfPath) => {
    if (!pdfPath) {
      showAlert("warning", "No certificate file available.");
      return;
    }
    window.open(`${BASE_URL}${pdfPath}`, "_blank");
  };

  // Function to check if application has any certificate
  const hasCertificate = (item) => {
    return item.ir_certificate_pdf || item.declaration_form_pdf;
  };

  // Function to get certificate label
  const getCertificateLabel = (item) => {
    if (item.ir_certificate_pdf && item.declaration_form_pdf) {
      return "Both Certificates";
    }
    if (item.ir_certificate_pdf) {
      return "IR Certificate";
    }
    if (item.declaration_form_pdf) {
      return "Declaration Form";
    }
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCreatedDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatQualifications = (qualifications) => {
    if (!qualifications) return "-";
    try {
      const q =
        typeof qualifications === "string"
          ? JSON.parse(qualifications)
          : qualifications;

      if (!Array.isArray(q)) return "-";

      return q.map((item, index) => (
        <div key={index} className="qualification-box mb-3">
          <div className="fw-bold text-dark">
            {item.degree || "N/A"} ({item.year || "N/A"})
          </div>
          <div className="text-muted small">
            {item.college || "N/A"}, {item.university || "N/A"}
          </div>
        </div>
      ));
    } catch (e) {
      return qualifications;
    }
  };

  const getShortMembership = (membership) => {
    if (!membership) return "-";
    if (membership.includes("Student")) return "Student Membership (SM)";
    if (membership.includes("Provisional")) return "Provisional / Life";
    if (membership.includes("Corporate")) return "Corporate Membership";
    if (membership.includes("Foreign")) return "Foreign Membership";
    if (membership.includes("SAARC")) return "SAARC Membership";
    return membership;
  };

  return (
    <Fragment>
      <PageTitle
        activeMenu="Membership Applications"
        motherMenu="Dashboard"
      />

      <Row>
        <Col lg={12}>
          {alert.show && (
            <Alert
              variant={alert.type}
              className="shadow-sm rounded-4 border-0 membership-alert"
            >
              {alert.text}
            </Alert>
          )}

          <Card className="shadow-sm border-0 rounded-4 membership-admin-card">
            <Card.Header className="membership-header border-0">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-4 w-100">
                <div>
                  <Card.Title className="mb-1 membership-title">
                    Membership Applications List
                  </Card.Title>
                  <div className="text-muted small">
                    Manage submitted membership applications professionally
                  </div>
                </div>

                <div className="membership-filter-wrap">
                  <InputGroup className="membership-search-group">
                    <Form.Control
                      type="text"
                      placeholder="Search by name, email, phone, membership..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="membership-control"
                    />
                  </InputGroup>

                  <Form.Select
                    value={membershipTypeFilter}
                    onChange={(e) => setMembershipTypeFilter(e.target.value)}
                    className="membership-control"
                  >
                    <option value="">All Membership Types</option>
                    {uniqueMembershipTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </Form.Select>

                  <Button
                    className="membership-refresh-btn"
                    onClick={fetchApplications}
                  >
                    <ArrowClockwise className="me-2" />
                    Refresh
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="membership-stats-row">
                <div className="membership-stat-box purple">
                  <span>Total</span>
                  <strong>{applications.length}</strong>
                </div>

                <div className="membership-stat-box green">
                  <span>Showing</span>
                  <strong>{filteredApplications.length}</strong>
                </div>
              </div>

              <div className="table-responsive">
                <Table className="membership-table align-middle">
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Applicant Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Membership Type</th>
                      <th>Medical Council No.</th>
                      <th>Total Amount</th>
                      <th>Application Date</th>
                      <th>Certificate</th>
                      <th style={{ minWidth: "210px" }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="10" className="text-center py-5">
                          Loading membership applications...
                        </td>
                      </tr>
                    ) : filteredApplications.length > 0 ? (
                      filteredApplications.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold text-dark">
                            {item.salutation || ""} {item.first_name || ""}{" "}
                            {item.middle_name || ""} {item.last_name || ""}
                          </td>
                          <td>{item.email || "-"}</td>
                          <td>{item.phone_number || "-"}</td>
                          <td>
                            <Badge className="membership-badge-type">
                              {getShortMembership(item.membership_type)}
                            </Badge>
                          </td>
                          <td>{item.medical_council_no || "-"}</td>
                          <td className="fw-semibold">
                            {item.total_amount || "-"}
                            {item.membership_type?.includes("Foreign")
                              ? " USD"
                              : " RS"}
                          </td>
                          <td>{formatCreatedDate(item.createdAt)}</td>
                          <td>
                            {/* Show IR Certificate for Full Members */}
                            {item.ir_certificate_pdf && (
                              <Button
                                className="certificate-btn mb-1"
                                size="sm"
                                onClick={() => downloadPDF(item.ir_certificate_pdf)}
                                style={{ display: 'block', width: '100%' }}
                              >
                                <FileEarmarkPdf className="me-2" />
                                IR Certificate
                              </Button>
                            )}
                            
                            {/* Show Declaration Form for Student Members */}
                            {item.declaration_form_pdf && (
                              <Button
                                className="certificate-btn"
                                size="sm"
                                onClick={() => downloadPDF(item.declaration_form_pdf)}
                                style={{ display: 'block', width: '100%' }}
                              >
                                <FileEarmarkPdf className="me-2" />
                                Declaration Form
                              </Button>
                            )}
                            
                            {/* If no certificates */}
                            {!item.ir_certificate_pdf && !item.declaration_form_pdf && (
                              <Badge bg="warning" text="dark">
                                Not Uploaded
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button
                                className="action-btn view-btn"
                                size="sm"
                                onClick={() => viewDetails(item)}
                              >
                                <Eye className="me-1" />
                                View
                              </Button>

                              <Button
                                className="action-btn edit-btn"
                                size="sm"
                                onClick={() => openEditModal(item)}
                              >
                                <PencilSquare className="me-1" />
                                Edit
                              </Button>

                              <Button
                                className="action-btn delete-btn"
                                size="sm"
                                onClick={() => confirmDelete(item.id)}
                              >
                                <Trash className="me-1" />
                                Delete
                              </Button>
                            </div>
                           </td>
                         </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-5">
                          No Membership Applications Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* VIEW DETAILS MODAL */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2">
            <PersonLinesFill />
            Membership Application Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3">
          {viewApplication && (
            <div className="application-details-wrapper">
              <Row className="g-4">
                <Col lg={6}>
                  <div className="detail-card">
                    <h5>Basic Information</h5>
                    <p>
                      <strong>Membership Type:</strong>{" "}
                      {viewApplication.membership_type || "-"}
                    </p>
                    <p>
                      <strong>Name:</strong> {viewApplication.salutation || ""}{" "}
                      {viewApplication.first_name || ""}{" "}
                      {viewApplication.middle_name || ""}{" "}
                      {viewApplication.last_name || ""}
                    </p>
                    <p>
                      <strong>Email:</strong> {viewApplication.email || "-"}
                    </p>
                    <p>
                      <strong>Phone:</strong>{" "}
                      {viewApplication.phone_number || "-"}
                    </p>
                    <p>
                      <strong>Alternate Phone:</strong>{" "}
                      {viewApplication.alternate_phone_number || "-"}
                    </p>
                    <p>
                      <strong>Gender:</strong> {viewApplication.gender || "-"}
                    </p>
                    <p>
                      <strong>Date of Birth:</strong>{" "}
                      {formatDate(viewApplication.date_of_birth)}
                    </p>
                    <p>
                      <strong>Address:</strong> {viewApplication.address || "-"}
                    </p>
                    <p>
                      <strong>City:</strong> {viewApplication.city || "-"}
                    </p>
                    <p>
                      <strong>State:</strong> {viewApplication.state || "-"}
                    </p>
                    <p>
                      <strong>Country:</strong> {viewApplication.country || "-"}
                    </p>
                    <p>
                      <strong>Zip Code:</strong>{" "}
                      {viewApplication.zip_code || "-"}
                    </p>
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="detail-card">
                    <h5>Professional Details</h5>
                    <p>
                      <strong>Current Designation:</strong>{" "}
                      {viewApplication.current_designation || "-"}
                    </p>
                    <p>
                      <strong>Medical Council No.:</strong>{" "}
                      {viewApplication.medical_council_no || "-"}
                    </p>
                    <p>
                      <strong>Affiliated Hospital:</strong>{" "}
                      {viewApplication.affiliated_hospital_name || "-"}
                    </p>
                    <p>
                      <strong>Hospital Address:</strong>{" "}
                      {viewApplication.affiliated_hospital_address || "-"}
                    </p>
                    <p>
                      <strong>Professional City:</strong>{" "}
                      {viewApplication.professional_city || "-"}
                    </p>
                    <p>
                      <strong>Professional State:</strong>{" "}
                      {viewApplication.professional_state || "-"}
                    </p>
                    <p>
                      <strong>Professional Zip Code:</strong>{" "}
                      {viewApplication.professional_zip_code || "-"}
                    </p>
                  </div>
                </Col>

                {/* Student Training Details Section */}
                {viewApplication.membership_type?.includes("Student") && (
                  <>
                    <Col lg={6}>
                      <div className="detail-card">
                        <h5>Training Details</h5>
                        <p>
                          <strong>IR Training Designation:</strong>{" "}
                          {viewApplication.ir_training_designation || "-"}
                        </p>
                        <p>
                          <strong>Training Year:</strong>{" "}
                          {viewApplication.training_year || "-"}
                        </p>
                        <p>
                          <strong>Training End Date:</strong>{" "}
                          {formatDate(viewApplication.training_end_date)}
                        </p>
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="detail-card">
                        <h5>Institute Details</h5>
                        <p>
                          <strong>Institute Name:</strong>{" "}
                          {viewApplication.institute_name || "-"}
                        </p>
                        <p>
                          <strong>Institute Address:</strong>{" "}
                          {viewApplication.institute_address || "-"}
                        </p>
                        <p>
                          <strong>Institute City:</strong>{" "}
                          {viewApplication.institute_city || "-"}
                        </p>
                        <p>
                          <strong>Institute State:</strong>{" "}
                          {viewApplication.institute_state || "-"}
                        </p>
                        <p>
                          <strong>Institute Zip Code:</strong>{" "}
                          {viewApplication.institute_zip_code || "-"}
                        </p>
                      </div>
                    </Col>
                  </>
                )}

                <Col lg={6}>
                  <div className="detail-card">
                    <h5>Qualifications</h5>
                    {formatQualifications(viewApplication.qualification_details)}
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="detail-card">
                    <h5>Payment Details</h5>
                    <p>
                      <strong>Membership Amount:</strong>{" "}
                      {viewApplication.membership_amount || "-"}
                    </p>
                    <p>
                      <strong>GST Amount:</strong>{" "}
                      {viewApplication.gst_amount || "-"}
                    </p>
                    <p>
                      <strong>Total Amount:</strong>{" "}
                      {viewApplication.total_amount || "-"}
                    </p>
                    <p>
                      <strong>Application Date:</strong>{" "}
                      {formatCreatedDate(viewApplication.createdAt)}
                    </p>
                    <p>
                      <strong>Terms Accepted:</strong>{" "}
                      {viewApplication.terms_accepted ? (
                        <Badge bg="success">Yes</Badge>
                      ) : (
                        <Badge bg="danger">No</Badge>
                      )}
                    </p>
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="detail-card">
                    <h5>President</h5>
                    <p>
                      <strong>Membership No.:</strong>{" "}
                      {viewApplication.recommender1_membership_no || "-"}
                    </p>
                    <p>
                      <strong>Name:</strong>{" "}
                      {viewApplication.recommender1_name || "-"}
                    </p>
                    <p>
                      <strong>Phone:</strong>{" "}
                      {viewApplication.recommender1_phone || "-"}
                    </p>
                    <p>
                      <strong>Email:</strong>{" "}
                      {viewApplication.recommender1_email || "-"}
                    </p>
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="detail-card">
                    <h5>Secretary</h5>
                    <p>
                      <strong>Membership No.:</strong>{" "}
                      {viewApplication.recommender2_membership_no || "-"}
                    </p>
                    <p>
                      <strong>Name:</strong>{" "}
                      {viewApplication.recommender2_name || "-"}
                    </p>
                    <p>
                      <strong>Phone:</strong>{" "}
                      {viewApplication.recommender2_phone || "-"}
                    </p>
                    <p>
                      <strong>Email:</strong>{" "}
                      {viewApplication.recommender2_email || "-"}
                    </p>
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="detail-card">
                    <h5>Certificates</h5>
                    {viewApplication.ir_certificate_pdf && (
                      <div className="mb-3">
                        <strong>IR Training Certificate:</strong><br />
                        <Button
                          className="certificate-btn mt-2"
                          onClick={() => downloadPDF(viewApplication.ir_certificate_pdf)}
                        >
                          <FileEarmarkPdf className="me-2" />
                          Download IR Certificate PDF
                        </Button>
                      </div>
                    )}
                    
                    {viewApplication.declaration_form_pdf && (
                      <div className="mb-3">
                        <strong>Declaration Form:</strong><br />
                        <Button
                          className="certificate-btn mt-2"
                          onClick={() => downloadPDF(viewApplication.declaration_form_pdf)}
                        >
                          <FileEarmarkPdf className="me-2" />
                          Download Declaration Form PDF
                        </Button>
                      </div>
                    )}
                    
                    {!viewApplication.ir_certificate_pdf && !viewApplication.declaration_form_pdf && (
                      <p className="text-muted mb-0">No certificates uploaded</p>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Edit Membership Application</Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3">
          {editApplication && (
            <Row className="g-4">
              <Col md={4}>
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  name="first_name"
                  value={editApplication.first_name || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Middle Name</Form.Label>
                <Form.Control
                  name="middle_name"
                  value={editApplication.middle_name || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  name="last_name"
                  value={editApplication.last_name || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  name="email"
                  value={editApplication.email || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  name="phone_number"
                  value={editApplication.phone_number || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Alternate Phone</Form.Label>
                <Form.Control
                  name="alternate_phone_number"
                  value={editApplication.alternate_phone_number || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={6}>
                <Form.Label>Membership Type</Form.Label>
                <Form.Control
                  name="membership_type"
                  value={editApplication.membership_type || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={6}>
                <Form.Label>Medical Council No.</Form.Label>
                <Form.Control
                  name="medical_council_no"
                  value={editApplication.medical_council_no || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={6}>
                <Form.Label>Current Designation</Form.Label>
                <Form.Control
                  name="current_designation"
                  value={editApplication.current_designation || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={6}>
                <Form.Label>Affiliated Hospital Name</Form.Label>
                <Form.Control
                  name="affiliated_hospital_name"
                  value={editApplication.affiliated_hospital_name || ""}
                  onChange={handleEditChange}
                />
              </Col>

              {/* Student specific fields in edit modal */}
              {editApplication.membership_type?.includes("Student") && (
                <>
                  <Col md={6}>
                    <Form.Label>IR Training Designation</Form.Label>
                    <Form.Control
                      name="ir_training_designation"
                      value={editApplication.ir_training_designation || ""}
                      onChange={handleEditChange}
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Label>Training Year</Form.Label>
                    <Form.Control
                      name="training_year"
                      value={editApplication.training_year || ""}
                      onChange={handleEditChange}
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Label>Institute Name</Form.Label>
                    <Form.Control
                      name="institute_name"
                      value={editApplication.institute_name || ""}
                      onChange={handleEditChange}
                    />
                  </Col>

                  <Col md={12}>
                    <Form.Label>Declaration Form PDF Path</Form.Label>
                    <Form.Control
                      name="declaration_form_pdf"
                      value={editApplication.declaration_form_pdf || ""}
                      onChange={handleEditChange}
                      placeholder="/uploads/filename.pdf"
                    />
                    <small className="text-muted">
                      Current: {editApplication.declaration_form_pdf || "None"}
                    </small>
                  </Col>
                </>
              )}

              {/* Full member specific fields in edit modal */}
              {!editApplication.membership_type?.includes("Student") && (
                <Col md={12}>
                  <Form.Label>IR Certificate PDF Path</Form.Label>
                  <Form.Control
                    name="ir_certificate_pdf"
                    value={editApplication.ir_certificate_pdf || ""}
                    onChange={handleEditChange}
                    placeholder="/uploads/filename.pdf"
                  />
                  <small className="text-muted">
                    Current: {editApplication.ir_certificate_pdf || "None"}
                  </small>
                </Col>
              )}

              <Col md={12}>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  name="address"
                  value={editApplication.address || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>City</Form.Label>
                <Form.Control
                  name="city"
                  value={editApplication.city || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>State</Form.Label>
                <Form.Control
                  name="state"
                  value={editApplication.state || ""}
                  onChange={handleEditChange}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Zip Code</Form.Label>
                <Form.Control
                  name="zip_code"
                  value={editApplication.zip_code || ""}
                  onChange={handleEditChange}
                />
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button onClick={updateApplication} disabled={editLoading}>
            {editLoading ? "Updating..." : "Update Application"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Membership Application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this membership application? This
          action cannot be undone. All applicant data will be permanently
          removed.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteApplication}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .membership-admin-card {
          overflow: hidden;
        }

        .membership-header {
          padding: 24px 28px 20px;
          background: #ffffff;
        }

        .membership-title {
          font-size: 28px !important;
          font-weight: 800 !important;
          color: #111827 !important;
        }

        .membership-filter-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }

        .membership-search-group {
          min-width: 340px;
        }

        .membership-control {
          min-height: 48px;
          border-radius: 14px !important;
          border: 1px solid #dbe3ef !important;
          box-shadow: none !important;
          font-size: 14px;
        }

        .membership-control:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 4px rgba(59,130,246,0.08) !important;
        }

        .membership-refresh-btn {
          min-height: 48px;
          border-radius: 14px !important;
          padding: 0 18px !important;
          background: linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%) !important;
          border: none !important;
          font-weight: 700 !important;
        }

        .membership-stats-row {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          margin-bottom: 18px;
          align-items: center;
        }

        .membership-stat-box {
          min-width: 90px;
          min-height: 38px;
          border-radius: 999px;
          padding: 8px 16px;
          color: #fff;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .membership-stat-box span {
          display: inline-block;
          font-size: 13px;
          font-weight: 700;
          opacity: 0.95;
          margin-bottom: 0;
          line-height: 1;
        }

        .membership-stat-box strong {
          font-size: 13px;
          font-weight: 800;
          line-height: 1;
        }
        
        .membership-stat-box.purple {
          background: linear-gradient(135deg, #7c3aed, #6d28d9);
        }

        .membership-stat-box.green {
          background: linear-gradient(135deg, #10b981, #059669);
        }

        .membership-stat-box.blue {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .membership-table {
          width: 100%;
          min-width: 1400px;
          border-collapse: separate;
          border-spacing: 0;
          overflow: hidden;
        }

        .membership-table thead th {
          background: #f3f6fb;
          color: #111827;
          font-size: 14px;
          font-weight: 700;
          padding: 16px 18px;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }

        .membership-table tbody td {
          padding: 18px;
          font-size: 14px;
          color: #4b5563;
          background: #fff;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: middle;
        }

        .membership-table tbody tr:hover td {
          background: #fafcff;
        }

        .membership-badge-type {
          background: linear-gradient(135deg, #4c1d95, #312e81) !important;
          color: #fff !important;
          font-size: 12px !important;
          padding: 8px 12px !important;
          border-radius: 999px !important;
          font-weight: 700 !important;
        }

        .certificate-btn {
          background: #f0fdf4 !important;
          border: 1px solid #86efac !important;
          color: #166534 !important;
          border-radius: 12px !important;
          font-weight: 700 !important;
          padding: 8px 14px !important;
        }

        .certificate-btn:hover {
          background: #dcfce7 !important;
          color: #166534 !important;
        }

        .action-btn {
          border: none !important;
          border-radius: 12px !important;
          font-weight: 700 !important;
          padding: 8px 14px !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
        }

        .view-btn {
          background: #eff6ff !important;
          color: #1d4ed8 !important;
        }

        .edit-btn {
          background: #fefce8 !important;
          color: #a16207 !important;
        }

        .delete-btn {
          background: #fef2f2 !important;
          color: #dc2626 !important;
        }

        .view-btn:hover {
          background: #dbeafe !important;
          color: #1d4ed8 !important;
        }

        .edit-btn:hover {
          background: #fef3c7 !important;
          color: #92400e !important;
        }

        .delete-btn:hover {
          background: #fee2e2 !important;
          color: #b91c1c !important;
        }

        .membership-alert {
          font-weight: 700;
        }

        .detail-card {
          background: #f8fbff;
          border: 1px solid #e5edf7;
          border-radius: 18px;
          padding: 22px;
          height: 100%;
          box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
        }

        .detail-card h5 {
          font-size: 20px;
          font-weight: 800;
          color: #111827;
          margin-bottom: 18px;
        }

        .detail-card p {
          margin-bottom: 12px;
          line-height: 1.7;
          color: #4b5563;
        }

        .qualification-box {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 14px 16px;
        }

        @media (max-width: 991px) {
          .membership-filter-wrap {
            width: 100%;
          }

          .membership-search-group {
            min-width: 100%;
          }

          .membership-control {
            width: 100%;
          }

          .membership-title {
            font-size: 24px !important;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default MembershipApplications;